import {
  Modal,
  Backdrop,
  Fade,
  Box,
  styled,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { separateThousands } from 'helpers/utils/number';
import { red } from '@mui/material/colors';

type LimitTransactionModalProps = {
  open: boolean;
  onClose: (open: boolean) => void;
};

function createData(name: string, minPerTransaction: number, maxPerTransaction: number, maxPerDay: number) {
  return { name, minPerTransaction, maxPerTransaction, maxPerDay };
}

const rows = [
  createData('Pemindahbukuan', 10_000, 1_000_000_000, 1_000_000_000),
  createData('Transfer SKN', 50_000, 500_000_000, 1_000_000_000),
  createData('Transfer RTGS', 100_000_001, 1_000_000_000, 1_000_000_000),
  createData('Total Transfer SKN + RTGS', 50_000, 2_000_000_000, 2_000_000_000),
  createData('Transfer Prima', 50_000, 50_000_000, 1_000_000_000),
  createData('Semua Pembelian', 20_000, 500_000_000, 500_000_000),
  createData('Semua Pembayaran', 25_000, 500_000_000, 500_000_000),
  createData('Payroll*', 100_000, 1_000_000_000, 1_000_000_000),
  createData('Sweep In*', 100_000, 1_000_000_000, 1_000_000_000),
  createData('Sweep Out*', 100_000, 1_000_000_000, 1_000_000_000),
  createData('Sweep Balance*', 100_000, 1_000_000_000, 1_000_000_000)
];

const LimitTransactionModal = ({ open, onClose }: LimitTransactionModalProps) => {
  const handleOnModalClick = () => {
    onClose(false);
  };

  return (
    <Modal
      closeAfterTransition
      open={open}
      onClose={handleOnModalClick}
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500
        }
      }}
    >
      <Fade in={open}>
        <ModalChildren>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              Limit Transaksi
            </Typography>
            <IconButton aria-label="close" onClick={handleOnModalClick}>
              <CloseIcon />
            </IconButton>
          </Box>
          <TableContainerStyled>
            <Table sx={{ minWidth: 650 }} size="small">
              <TableHead>
                <TableRow>
                  <TableHeaderRowCell>JENIS TRANSAKSI</TableHeaderRowCell>
                  <TableHeaderRowCell align="right"></TableHeaderRowCell>
                  <TableHeaderRowCell align="right">MINIMAL PER TRANSAKSI</TableHeaderRowCell>
                  <TableHeaderRowCell align="right"></TableHeaderRowCell>
                  <TableHeaderRowCell align="right">MAXIMAL PER TRANSAKSI</TableHeaderRowCell>
                  <TableHeaderRowCell align="right"></TableHeaderRowCell>
                  <TableHeaderRowCell align="right">MAXIMAL PER HARI</TableHeaderRowCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableBodyRowCell component="th" scope="row">
                      {row.name.slice(-1) === '*' ? (
                        <>
                          {row.name.slice(0, -1)}
                          <Typography component={'span'} sx={{ color: red[500] }}>
                            *
                          </Typography>
                        </>
                      ) : (
                        row.name
                      )}
                    </TableBodyRowCell>
                    <TableBodyRowCell align="right">Rp</TableBodyRowCell>
                    <TableBodyRowCell align="right">{separateThousands(row.minPerTransaction)}</TableBodyRowCell>
                    <TableBodyRowCell align="right">Rp</TableBodyRowCell>
                    <TableBodyRowCell align="right">{separateThousands(row.maxPerTransaction)}</TableBodyRowCell>
                    <TableBodyRowCell align="right">Rp</TableBodyRowCell>
                    <TableBodyRowCell align="right">{separateThousands(row.maxPerDay)}</TableBodyRowCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainerStyled>
          <Typography variant="caption" component="p" sx={{ mt: 3, width: '100%', color: 'surface.700' }}>
            <Typography component={'span'} sx={{ color: red[500] }}>
              *
            </Typography>{' '}
            Menu baru bisa dimunculkan jika sudah ada formulir/perjanjian kerjasama terkait
          </Typography>
        </ModalChildren>
      </Fade>
    </Modal>
  );
};

const ModalChildren = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  padding: 32,
  borderRadius: 4,
  backgroundColor: theme.palette.background.paper,

  '&:focus': {
    outline: 'none'
  }
}));

const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
  border: `1px solid ${theme.palette.surface[100]}}`,

  ':is(th, td).MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.surface[100]}}`
  }
}));

const TableHeaderRowCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: 10,
  color: theme.palette.surface[500]
}));

const TableBodyRowCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: 12,
  color: theme.palette.surface[700]
}));

export default LimitTransactionModal;
